import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Category, CategoryIcon } from '@browsing/filters';

import { CategoryTabIds } from '@shared/constants';
import { Tab } from '@shared/types/mylounge/ccfEntries';
import { Color } from '@lux/tokens';
import {
    getGA4Context,
    trackConfirmFilter,
    trackSelectFilter,
    trackViewFilter,
} from '@browsing/tracking';
import { ElementType } from '@pegasus/tracker';

import * as S from './index.styles';
import { useAllCategories, useCCFEntries } from '../hooks';
import { findCategoryById, redirectToCrossCampaignCatalog } from '../utils';

const MAX_NUMBER_OF_CATEGORIES_SHOWN = 6;

interface KidsCategoriesProps {
    categories: Tab['elements'];
    selectedKidsCategoryId: number;
    setSelectedKidsCategoryId: (selectedKidsCategoryId: number) => void;
}

const KidsCategories = ({
    categories,
    selectedKidsCategoryId,
    setSelectedKidsCategoryId,
}: KidsCategoriesProps) => (
    <ul>
        {categories.map(kidsCategory => (
            <li key={kidsCategory.id}>
                <S.Button
                    type="button"
                    role="button"
                    onClick={() => setSelectedKidsCategoryId(kidsCategory.id)}
                    tabIndex={0}
                >
                    <S.HeadingCategory
                        key={kidsCategory.id}
                        color={kidsCategory.id === selectedKidsCategoryId && Color.primary}
                    >
                        {kidsCategory.name}
                    </S.HeadingCategory>
                </S.Button>
            </li>
        ))}
    </ul>
);

interface Level3CategoriesProps {
    categories: Category[];
    categoryLevel1Id: number;
}

const Level3Categories = ({ categoryLevel1Id, categories }: Level3CategoriesProps) =>
    categories.map(categoryLevel3 => (
        <S.Category key={categoryLevel3.id}>
            <S.Button
                role="link"
                tabIndex={0}
                onClick={() => {
                    const trackingArgs = {
                        context: getGA4Context({ isCrossCampaign: true }),
                        element: 'category_filter' as ElementType,
                        filterLvl: 3,
                        filtersValue: `${categoryLevel1Id};${categoryLevel3.parentId};${categoryLevel3.id}`,
                        length: categoryLevel3.count,
                    };

                    trackSelectFilter(trackingArgs);
                    trackConfirmFilter(trackingArgs);
                    redirectToCrossCampaignCatalog(categoryLevel3.id);
                }}
            >
                <S.CategoryText>{categoryLevel3.name}</S.CategoryText>
            </S.Button>
        </S.Category>
    ));

interface Level2CategoriesProps {
    categories: Category[];
    getNextLevelCategories: (categoryId: number) => Category[];
}

const Level2Categories = ({ categories, getNextLevelCategories }: Level2CategoriesProps) =>
    categories.map(category => (
        <ul key={category.id}>
            <li>
                <S.Button
                    type="button"
                    role="link"
                    tabIndex={0}
                    onClick={() => {
                        const trackingArgs = {
                            context: getGA4Context({ isCrossCampaign: true }),
                            element: 'category_filter' as ElementType,
                            filterLvl: 2,
                            filtersValue: `${category.parentId};${category.id}`,
                            length: category.count,
                        };

                        trackSelectFilter(trackingArgs);
                        trackConfirmFilter(trackingArgs);
                        redirectToCrossCampaignCatalog(category.id);
                    }}
                >
                    <S.HeadingCategory>
                        <CategoryIcon id={category.id} />
                        {category.name}
                    </S.HeadingCategory>
                </S.Button>
            </li>
            <Level3Categories
                categoryLevel1Id={category.parentId}
                categories={getNextLevelCategories(category.id)}
            />
        </ul>
    ));

interface Props {
    selectedTabId: CategoryTabIds;
    showMenu: (tabId: CategoryTabIds) => void;
    hideMenu: () => void;
}

export const CrossCampaignEntryLargeScreen = ({ selectedTabId, showMenu, hideMenu }: Props) => {
    const sharedGA4args = {
        context: getGA4Context({ isCrossCampaign: true }),
        element: 'category_filter' as ElementType,
    };
    const allCategories = useAllCategories();
    const ccfEntriesTabs = useCCFEntries();
    const ccfEntry = ccfEntriesTabs?.find(({ tabId }) => tabId === selectedTabId);
    const isKidsTab = selectedTabId === CategoryTabIds.KIDS;
    const [selectedKidsCategoryId, setSelectedKidsCategoryId] = useState<number | null>(null);
    const tabCategories = useMemo(() => ccfEntry?.elements || [], [ccfEntry?.elements]);
    const lastFocusedElementRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!ccfEntry?.elements?.length) {
            return;
        }

        // this can be optimized when the feature is fully released.
        const level1CategoryId = findCategoryById({
            category: allCategories,
            id: ccfEntry.elements[0].id,
        })?.parentId;
        const level1Category = findCategoryById({ category: allCategories, id: level1CategoryId });

        trackViewFilter({
            ...sharedGA4args,
            filterLvl: isKidsTab ? 1 : 2,
            filtersValue: `${level1CategoryId}`,
            length: level1Category?.count || 0,
        });
    }, []);

    useEffect(() => {
        if (isKidsTab && tabCategories.length > 0) {
            setSelectedKidsCategoryId(tabCategories[0].id);
        }
    }, [isKidsTab, tabCategories]);

    useEffect(() => {
        if (!selectedTabId) return;

        lastFocusedElementRef.current = document.activeElement as HTMLElement;
    }, [selectedTabId]);

    const getNextLevelCategories = (categoryId: number) => {
        const category = findCategoryById({
            category: allCategories,
            id: categoryId,
        });

        if (!category || !category?.categories?.length) {
            return [];
        }

        return (
            category?.categories
                ?.filter(subCategory => subCategory.count > 0)
                .sort((a, b) => b.count - a.count)
                .slice(0, MAX_NUMBER_OF_CATEGORIES_SHOWN) || []
        );
    };

    if (!allCategories?.categories?.length || !ccfEntriesTabs?.length || !ccfEntry) {
        return null;
    }

    const level2Categories = isKidsTab
        ? getNextLevelCategories(selectedKidsCategoryId)
        : tabCategories
              .map(tabCategory => findCategoryById({ id: tabCategory.id, category: allCategories }))
              .filter(Boolean);

    const selectKidsCategoryId = (categoryId: number) => {
        trackSelectFilter({
            ...sharedGA4args,
            filterLvl: 1,
            filtersValue: `${categoryId}`,
            length: findCategoryById({ category: allCategories, id: categoryId })?.count,
        });
        setSelectedKidsCategoryId(categoryId);
    };

    return (
        !!selectedTabId && (
            <S.Menu
                data-testid="menu"
                tabIndex={-1}
                onKeyDown={event => {
                    if (event.key === 'Escape') {
                        lastFocusedElementRef.current?.focus();

                        hideMenu();
                    }
                }}
                onMouseEnter={() => showMenu(selectedTabId)}
                onMouseLeave={hideMenu}
            >
                <S.Wrapper>
                    <S.CategoryMenu role="group">
                        {isKidsTab && (
                            <KidsCategories
                                categories={tabCategories}
                                selectedKidsCategoryId={selectedKidsCategoryId}
                                setSelectedKidsCategoryId={selectKidsCategoryId}
                            />
                        )}
                        <Level2Categories
                            categories={level2Categories}
                            getNextLevelCategories={getNextLevelCategories}
                        />
                    </S.CategoryMenu>
                </S.Wrapper>
            </S.Menu>
        )
    );
};
